// The capability TOKEN vocabulary — what a box advertises it can run: the named
// tokens, the three token constructors (`toolchain:`, `backend:`, `service:`),
// the reserved namespaces a box may not claim for itself, the stored
// `runners.capabilities` shape and the gate that matches a required set against it.
// Split out of `../runner.ts`, which is now the barrel over this directory.

import { z } from 'zod';

/**
 * Well-known capability tokens a box advertises in `capabilities.provisions` and
 * a task names in its `requires` — the two sides the fleet assigner matches
 * (arc 101 slice 04, `dispatch/select.ts`). Setup mode DETECTS a repo's needs
 * (`detectRepoNeeds`) and provisions them, so the box that ran a repo's setup
 * carries the tokens that repo's tasks require. The vocabulary is OPEN (a plain
 * string token, not a closed enum) so a new need never forces a domain change —
 * these are the ones setup emits today:
 *   - `browsers`      — a browser is installed for UI/e2e testing (Playwright / Cypress).
 *   - `display`       — an X11 / xvfb virtual display for headful UI testing.
 *   - `docker`        — a working Docker daemon (a `Dockerfile` or a compose file
 *     in the repo). Unreachable until #2056: it was listed here as a token setup
 *     emits while `detectRepoNeeds` emitted no such thing.
 *   - `cache:deps`    — a warm dependency cache (pinned lockfile → fast cold installs).
 * plus `toolchain:<stack>` for the repo's language toolchain (see
 * {@link toolchainCapability}). One token is NOT a setup-detected repo need but a
 * box-inherent one: `ci` marks a box that CAN backfill CI jobs while idle (arc 101
 * slice 04 idle-CI reuse) — queued CI still yields to any ready ai-dev task and a
 * running job is never preempted (`admitCiBackfill` in `dispatch/select.ts`). Setup
 * emits it when the box's CI container runtime answers (see
 * {@link RunnerCapability.Ci}).
 * Setup also advertises two box-INHERENT tokens (arc 101 slice 06a) — WHAT
 * inference the box can reach, distinct from a repo's build needs:
 *   - `subscription:claude` — the box's `claude` CLI is logged in with a
 *     subscription (a Max/Pro OAuth login, not an API key), so it can code without
 *     a BYOK key (see {@link RunnerCapability.SubscriptionClaude}).
 *   - `backend:<kind>`      — the box's configured backend env-profile
 *     (`DZ_BACKEND_KIND`, anything but `none`; see {@link backendCapability}).
 *   - `isolation:container` — the box runs its coding adapter in a per-job container
 *     (arc 101 slice 06b), so a shared/user box may safely co-reside across tenants
 *     (see {@link RunnerCapability.IsolatedAdapter}).
 * `capabilitiesSatisfy` compares raw tokens, so an operator-provisioned token the
 * platform hasn't named still matches a task that asks for it.
 */
export const RunnerCapability = {
  Browsers: 'browsers',
  Display: 'display',
  Docker: 'docker',
  CacheDeps: 'cache:deps',
  /**
   * Box backfills CI jobs while idle (arc 101 slice 04); yields to ai-dev, never
   * preempts. A CAPABILITY, not a preference: setup mode emits it when the box's
   * configured CI container runtime answers a spawn probe (`ciRuntimeReachable`,
   * `apps/runner/src/modes/setup`) — the same binary `ci-exec.ts` would exec — so a
   * box advertising `ci` is one whose next `<runtime> run` starts. That advert
   * (`boxCapabilities` → `capabilities_detected` → `mergeRunnerProvisions`) is the
   * ONLY writer of `runners.capabilities`, so a box that never ran setup advertises
   * nothing, and `box fleet_status` is where an operator confirms the token landed.
   *
   * **It was gated on an env var from #2056 until arc 101 slice 03, and that gate
   * never opened.** Before #2056 nothing emitted the token at all (the column was
   * `{}` on every row for the lifetime of the table). #2056 made `DZ_CI_BACKFILL=1`
   * in `/etc/dz-runner/env` emit it — but nothing in the tree WROTE that var (not
   * `/join`, not `enroll-vm.sh`, not `attach-vm.sh`), so prod on 2026-08-30 still
   * read 27 runners / 0 with `ci` / 0 `ci_jobs` rows. Arc 101 slice 03 deleted the var rather
   * than teaching provisioning to write it: a role pin written onto every enrolled
   * box by a provisioning script is the `FLEET_PER_ASSIGNMENT_ROLE` mistake
   * (#1418/#1982), invisible to a repo-only audit. The customer's opt-in is the
   * `runs-on: developerz-Nvcpu` line in their own workflow
   * (`DEVELOPERZ_LABEL_PREFIX`, `./ci-job`), fenced by the account's `ciCeiling`
   * entitlement — both auditable, unlike a hand-edited box env.
   */
  Ci: 'ci',
  /**
   * The box's `claude` CLI is logged in with a subscription (a Max/Pro OAuth login,
   * not an API key), detected by setup mode (arc 101 slice 06a) — so a task pinned to
   * it lands only on a box that can code without a BYOK key.
   */
  SubscriptionClaude: 'subscription:claude',
  /**
   * The box CLAIMS it runs its coding adapter INSIDE a per-job container (arc 101
   * slice 06b, runner flag `DZ_ISOLATE_ADAPTER`).
   *
   * **A claim, never a grant — and it is PRIVILEGED, so no box may advert it.**
   * Cross-tenant scope breadth is decided by `runners.isolation_attested_at`, an
   * operator-written column no box can reach; this token grants nothing. It used
   * to BE the gate: `resolveCodingScope` widened a `user`/`shared` box's claim
   * scope on `capabilitiesSatisfy(capabilities, [isolation:container])`, and
   * `capabilities` is written from the box's own `capabilities_detected` advert —
   * so a box asserted its own containment and the platform believed it. Listed in
   * {@link PRIVILEGED_PROVISIONS}: refused at the write ({@link mergeProvisions})
   * and dropped at the read ({@link effectiveProvisions}).
   */
  IsolatedAdapter: 'isolation:container',
  /**
   * The box can UNWRAP a sealed credential handed on an assignment (#1505) — it
   * carries its own per-box vault subkey (`DZ_BOX_KEK_BASE64`, #1300/#1307).
   * Unlike every token above it, this one is **platform-DERIVED, never advertised**:
   * see {@link boxKekCapable} / {@link effectiveProvisions}.
   */
  SealedSecrets: 'secrets:sealed',
  /**
   * The box APPLIES an assignment's `agent_mode` tool narrowing (plan 101 slice 06).
   * Platform-DERIVED from the reported build ({@link boxModeNarrowingCapable} /
   * {@link effectiveProvisions}), never advertised: a box that claimed it falsely
   * would be handed a narrowed mode and run it with every tool.
   */
  ModeNarrowing: 'harness:mode-narrowing',
  /**
   * The box may be handed a never-planned coding task WITHOUT its declared area lock
   * (#5370): its team lane takes the lock on its planned set (`files_plan`) before any
   * editor writes, and gives the claim back — never edits — when the lock is held or
   * unanswered on an `assignment.plan_admitted` claim. Platform-DERIVED from the reported
   * build ({@link boxPlanAdmissionCapable}), never advertised: a box that claimed it
   * falsely would be handed a lock-free claim and edit files another box holds.
   */
  PlanAdmission: 'harness:plan-admission',
  /**
   * The box can answer a MODEL-CALL OFFER (#6759, epic #6552): fetch one composed prompt,
   * make the call on the sealed credential bound to the cluster's pick, and post the
   * result before the job's deadline. Required by every `triage_judge` / `kb_fold` task
   * (`requiredCapabilitiesForOrigin`). Platform-DERIVED, never advertised, like
   * {@link RunnerCapability.ModeNarrowing}: a box that claimed it falsely would be handed a
   * prompt it cannot answer. Derived from the reported build alone
   * (`boxModelCallCapable`, `RUNNER_MODEL_CALL_MIN_VERSION`, #6760).
   */
  ModelCall: 'harness:model-call',
  /**
   * The box proposes the policy a person chose at the enable (#7802,
   * `maintenance_recipe`): a build older than the field strips it and proposes `auto_merge:
   * true` for someone who chose `review_only`. Platform-DERIVED from the reported build
   * (`boxSetupRecipesCapable`). Spelled `SETUP_RECIPE_CAPABILITY` where the envelope names it.
   */
  SetupRecipes: 'harness:setup-recipes',
  /**
   * The box can answer a PLAN call (#7569, epic #6552): the Planner's model-call offer
   * (`plan_author`), whose structured-output schema only a build that knows it can answer.
   * Required by every `plan_author` task beside {@link RunnerCapability.ModelCall}
   * (`requiredCapabilitiesForOrigin`). Platform-DERIVED, never advertised: a build at or
   * above `RUNNER_PLANNER_MIN_VERSION` derives it (`boxPlannerCapable`, #6765).
   */
  Planner: 'harness:planner',
} as const;
export type RunnerCapability = (typeof RunnerCapability)[keyof typeof RunnerCapability];

/** The `toolchain:<stack>` capability token for a language toolchain (e.g. `toolchain:python`). */
export function toolchainCapability(stack: string): string {
  return `toolchain:${stack}`;
}

/**
 * The `backend:<kind>` capability token for a box's configured inference backend
 * env-profile (`DZ_BACKEND_KIND`: `anthropic` / `anthropic-compatible` /
 * `openai-compatible`). Mirrors {@link toolchainCapability} — a `<kind>`-parameterized
 * token so a new backend needs no domain change. The `none` kind (a box that manages
 * its own inference env) advertises no backend token; the caller is responsible for
 * not minting one for it.
 */
export function backendCapability(kind: string): string {
  return `backend:${kind}`;
}

/**
 * A SERVICE a repo's own verify gate needs running before it can grade anything —
 * the closed vocabulary a `.maintainer.yml` `services:` block may name (#2919).
 *
 * CLOSED, and that is a security boundary rather than a convenience. A free-form
 * image reference would make a stranger's `.maintainer.yml` a way to start a
 * process on our boxes; a fixed enum means the worst a declaration can do is make
 * its own tasks unclaimable.
 *
 * The two members are the two we have MEASURED, not a guess at what repos might
 * want: `developerz-ai/wurk`'s `bin/check` refuses without a Redis (#2918), and
 * `verifyVerdict`'s own docblock records 6 tesote runs killed by a missing
 * Postgres. A third earns its place the same way.
 */
export const REPO_SERVICES = ['redis', 'postgres'] as const;
export type RepoService = (typeof REPO_SERVICES)[number];

/** Edge/read validator for a declared service name. */
export const RepoServiceSchema = z.enum(REPO_SERVICES);

/**
 * The capability token a box adverts when it can reach {@link RepoService}.
 *
 * `service:*` is UNPRIVILEGED on purpose — it is a reachability fact a box can
 * prove by connecting, exactly like `ci` (`ciRuntimeReachable` spawns the runtime
 * rather than reading a flag), and unlike `isolation:container`, which is a
 * TENANCY claim no box-side probe is admissible evidence for. So it belongs to the
 * box's own advert and is deliberately absent from
 * {@link PRIVILEGED_PROVISION_NAMESPACES}.
 */
export const SERVICE_CAPABILITY_PREFIX = 'service:' as const;

/**
 * The `service:` token prefix above is exported because the CLAIM gate builds these
 * tokens in SQL (`repoServicesRequiredCase`, `@developerz/db`) where this function
 * cannot run. One constant, both sides — a literal in the SQL would be a second
 * spelling of the namespace, and `capabilitiesSatisfy` is an exact match, so a drift
 * would strand every declaring repo's tasks with nothing red anywhere.
 */
export function serviceCapability(service: RepoService): string {
  return `${SERVICE_CAPABILITY_PREFIX}${service}`;
}

/**
 * Capability NAMESPACES the platform reserves: no box may advert a token in one,
 * and a stored token in one is never honoured. A namespace is the segment before
 * the first `:` ({@link toolchainCapability} / {@link backendCapability} mint the
 * unreserved ones), matched case-insensitively because `CapabilityTokenSchema`
 * carries `/i` and a legacy row may hold `ISOLATION:CONTAINER`.
 *
 * **Reserved by NAMESPACE, not by token, because the vocabulary is OPEN.** The
 * wire schema is a SHAPE gate over an open set, so an allow-list of known-safe
 * tokens would refuse every future `toolchain:<stack>` nobody has named yet, and
 * a deny-list of known-bad TOKENS fails open on the next one somebody adds —
 * `isolation:vm` would sail through a list that spells out `isolation:container`.
 * Reserving the namespace makes the privileged side CLOSED (nothing new can be
 * self-asserted into it) while the build-need side stays open, which is the only
 * split that fails closed without freezing the vocabulary.
 *
 * The line is TENANCY-OR-TRUST, and it is drawn per token below, not by feel:
 *
 *   - `isolation:` — decides whether a box may serve MORE THAN ONE TENANT. A box
 *     that grants itself this reads another tenant's sealed BYOK keys, git
 *     credentials and `repo_env` secrets, because the assigner re-targets sealed
 *     material to the CLAIMING box's ring. Tenancy: reserved.
 *   - `secrets:` — asserts the box can UNWRAP sealed credentials (#1505/#1510).
 *     Trust: reserved. The read filter was always here; the matching write
 *     refusal was built and cut, and is now built.
 *
 * Everything setup detects today stays self-assertable, and each for a reason:
 * `browsers` / `display` / `docker` / `cache:deps` / `toolchain:<stack>` are REPO
 * BUILD NEEDS — a false advert only lands work the box then fails, on its own
 * account. `backend:<kind>` and `subscription:claude` describe the box's own
 * inference env, and a false one costs the box a run it cannot serve, never a
 * secret it should not hold. `ci` looks like an exception and is not: the CI it
 * admits the box to is its OWN account's (`claimCiForBox` is account-fenced), it
 * runs only what that account's own workflow labelled for the fleet, and the
 * capability's only mechanism IS the box's advert — reserving it would delete CI
 * backfill (#2056) and buy no tenancy.
 */
export const PRIVILEGED_PROVISION_NAMESPACES: readonly string[] = [
  'isolation',
  'secrets',
  'harness',
];

/** The reserved tokens the platform names today — every one inside a reserved namespace. */
export const PRIVILEGED_PROVISIONS: readonly string[] = [
  RunnerCapability.IsolatedAdapter,
  RunnerCapability.SealedSecrets,
  RunnerCapability.ModeNarrowing,
  RunnerCapability.PlanAdmission,
  RunnerCapability.ModelCall,
  RunnerCapability.Planner,
];

/**
 * The reserved tokens NO build is derived yet: a token named a release before the build
 * that earns it (`harness:model-call` was one, #6759, until #6760 derived it). An origin
 * that requires one is claimable by no machine, so no surface may offer it as a machine's
 * role (`originClaimableToday`). The runner's reachability guard
 * (`apps/runner/src/modes/setup/base/capabilities.test.ts`, `not-yet-derived`) pins this
 * list to the tokens `effectiveProvisions` derives for no build; the release that wires a
 * derivation removes its token here. The list is empty today: #6765 derived
 * `harness:planner`, the last token named before its build.
 */
export const NOT_YET_DERIVED_PROVISIONS: readonly string[] = [];

/**
 * Is this token one a box may never advert about itself? True for anything in a
 * {@link PRIVILEGED_PROVISION_NAMESPACES} namespace, OR an exact
 * {@link PRIVILEGED_PROVISIONS} member. Both arms, not just the namespace one, so
 * that reserving a future BARE token is a one-line list edit and not a silent
 * no-op — the namespace rule cannot see a token with no `:` in it.
 *
 * An unnamespaced token that is not listed is a build need and passes: that is the
 * open half of the vocabulary, and it stays safe because reaching a reserved
 * namespace requires spelling the reserved prefix, never an accident of shape.
 */
export function isPrivilegedProvision(token: string): boolean {
  const folded = token.trim().toLowerCase();
  if (PRIVILEGED_PROVISIONS.includes(folded)) return true;
  const colon = folded.indexOf(':');
  if (colon === -1) return false;
  return PRIVILEGED_PROVISION_NAMESPACES.includes(folded.slice(0, colon));
}

/**
 * What a runner can already do. `repos` are the slugs pre-provisioned on the box
 * (skip the cold-clone cost); `provisions` are the richer capability tokens setup
 * mode detected the box's repos NEED and provisioned (browsers, an X11 display, a
 * language toolchain, warm caches — see {@link RunnerCapability}). The fleet
 * assigner matches a task's `requires` against `provisions` (`capabilitiesSatisfy`),
 * so a UI test only lands on a box with browsers + a display. Both default to `[]`
 * — a bare `{}` row (a plainly-provisioned box) parses to `{ repos: [], provisions: [] }`.
 */
export const RunnerCapabilitiesSchema = z.object({
  /** Repo slugs (`owner/name`) checked out + set up on the box. */
  repos: z.array(z.string()).default([]),
  /**
   * Capability tokens the box provides beyond a bare checkout — what setup mode
   * detected + provisioned. Empty on a plainly-provisioned box. Matched against a
   * task's `requires` by {@link capabilitiesSatisfy}.
   */
  provisions: z.array(z.string()).default([]),
});
export type RunnerCapabilities = z.infer<typeof RunnerCapabilitiesSchema>;

/**
 * Does a box with these `capabilities` satisfy a task's `required` capability
 * tokens? True iff every required token is in the box's `provisions` — set
 * containment, order-insensitive. An empty `required` is satisfied by ANY box
 * (an unconstrained task runs anywhere), so a task that names no capability is
 * never stranded. Pure — the match primitive the fleet assigner
 * (`dispatch/select.ts`) and any capability gate share, so "can this box run this
 * task" is decided one way everywhere.
 *
 * A caller matching ONE box against many rows (the assigner's per-box demand walk,
 * `apps/worker/src/dispatch/coding-demand.ts`) passes the box's provisions as a
 * prebuilt set, built once for the walk instead of once per row.
 */
export function capabilitiesSatisfy(
  capabilities: RunnerCapabilities | ReadonlySet<string>,
  required: readonly string[],
): boolean {
  if (required.length === 0) return true;
  const provided = 'provisions' in capabilities ? new Set(capabilities.provisions) : capabilities;
  return required.every((token) => provided.has(token));
}
